/**
 * useAppUpdater — Tauri 2 auto-update lifecycle (May 30, 2026).
 *
 * Wraps ``@tauri-apps/plugin-updater`` + ``@tauri-apps/plugin-process``
 * behind a stable, fully-typed React hook so the SPA can render a
 * non-blocking "Update available" banner without the wizard-style modal
 * dialogs Tauri's built-in flow defaults to.
 *
 * Lifecycle:
 *   1. **Mount**: schedule a silent check (1.5s post-paint, then every
 *      ``RECHECK_INTERVAL_MS``). Tauri's updater hits the signed
 *      ``latest.json`` on GitHub Releases → returns ``null`` if up-to-date
 *      or an ``Update`` handle.
 *   2. **Available**: surface ``state = "available"`` so a host component
 *      can render the banner. NO download starts automatically — the user
 *      opts in (Granola / Linear pattern, not "force restart in 5s").
 *   3. **User clicks Aggiorna**: ``downloadAndInstall()`` streams the
 *      signed bundle + verifies the ed25519 signature against the pubkey
 *      baked into ``tauri.conf.json`` (the plugin throws on mismatch).
 *      Progress events update ``downloaded`` / ``contentLength``.
 *   4. **Done**: state flips to ``ready_to_restart``; UI shows the
 *      "Riavvia ora" button. ``relaunch()`` from plugin-process closes
 *      the current process gracefully and re-launches the new binary.
 *
 * Non-Tauri environments (browser preview, vitest) short-circuit to
 * ``state = "unsupported"`` instead of throwing — the host renders nothing.
 *
 * Signature verification is non-negotiable: the plugin refuses to apply
 * any update whose ed25519 signature doesn't match the pubkey. Loss of
 * the private key ⇒ updates stop working until a new pubkey ships in
 * an app version.
 */

import * as React from "react";

const RECHECK_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h between silent checks
const INITIAL_CHECK_DELAY_MS = 1500; // let the SPA finish hydration first

export type AppUpdaterState =
  | "idle" //          haven't checked yet (or non-Tauri env warming up)
  | "checking" //      ``check()`` in flight
  | "up_to_date" //    ``check()`` resolved with no update
  | "available" //     update found, awaiting user confirmation
  | "downloading" //   ``downloadAndInstall()`` in flight
  | "ready_to_restart" // download + install done, awaiting ``relaunch()``
  | "error" //         transient error — caller can retry via ``check()``
  | "unsupported"; //  running outside Tauri (browser preview / vitest)

export interface AppUpdateInfo {
  readonly version: string;
  readonly currentVersion: string;
  readonly releaseDate?: string | null;
  readonly notes?: string | null;
}

export interface UseAppUpdaterResult {
  readonly state: AppUpdaterState;
  readonly update: AppUpdateInfo | null;
  readonly downloaded: number;
  readonly contentLength: number | null;
  readonly error: string | null;
  /** Force a re-check now — surfaces a no-op when ``state === "downloading"``.
   * Pass ``{ silent: true }`` for background checks that must not raise an
   * error toast on failure (used by the mount + interval auto-checks). */
  readonly check: (opts?: { silent?: boolean }) => Promise<void>;
  /** Begin the streamed download + signature verify + install. */
  readonly installAndRestart: () => Promise<void>;
  /** Dismiss the banner for this session (until next ``check()``). */
  readonly dismiss: () => void;
}

interface UpdaterModule {
  check: typeof import("@tauri-apps/plugin-updater").check;
}
interface ProcessModule {
  relaunch: typeof import("@tauri-apps/plugin-process").relaunch;
}

/**
 * Detect the "Tauri IPC not initialized" failure mode.
 *
 * Tauri plugins reach the host process through ``window.__TAURI_INTERNALS__
 * .invoke``. When that global is missing — browser preview, an embedded
 * webview that hasn't finished hand-shaking, the SPA cached after a
 * Tauri runtime upgrade — the plugin throws TypeError "Cannot read
 * properties of undefined (reading 'invoke')". That's not an update
 * failure; we just can't reach the host. Surface it as ``unsupported``
 * so the user gets no red toast, only silent absence of the banner.
 *
 * Matches both the literal Chrome message and the Safari variant
 * ("undefined is not an object (evaluating '...invoke')").
 */
function isTauriIpcMissing(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /invoke/i.test(msg) && /undefined|null/i.test(msg);
}

/**
 * Lazily resolves the Tauri plugins so the SPA bundle stays usable in
 * browser preview (`vite preview`, Storybook, vitest). Dynamic imports
 * fail there → we flip the hook to ``unsupported`` and render nothing.
 */
async function loadTauriPlugins(): Promise<{
  updater: UpdaterModule;
  process: ProcessModule;
} | null> {
  try {
    const [updater, process] = await Promise.all([
      import("@tauri-apps/plugin-updater"),
      import("@tauri-apps/plugin-process"),
    ]);
    return { updater, process };
  } catch {
    return null;
  }
}

export function useAppUpdater(): UseAppUpdaterResult {
  const [state, setState] = React.useState<AppUpdaterState>("idle");
  const [update, setUpdate] = React.useState<AppUpdateInfo | null>(null);
  const [downloaded, setDownloaded] = React.useState(0);
  const [contentLength, setContentLength] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // The ``Update`` handle returned by ``check()`` carries the resolver +
  // signature verifier. We need to hold it across renders so the user's
  // click on "Aggiorna" picks up the same instance.
  const updateRef = React.useRef<Awaited<
    ReturnType<UpdaterModule["check"]>
  > | null>(null);
  const dismissedVersionRef = React.useRef<string | null>(null);
  const pluginsRef = React.useRef<Awaited<
    ReturnType<typeof loadTauriPlugins>
  > | null>(null);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const check = React.useCallback(async (opts?: { silent?: boolean }): Promise<void> => {
    // ``silent`` = background/auto check (mount + 6h timer). A failed
    // background check (no release published yet, BE 404/502, network down)
    // must NOT raise a red toast — the user didn't ask. We log + fall back to
    // ``up_to_date`` so the banner stays hidden (Linear/Granola pattern).
    // Only USER-initiated checks (no ``silent``) surface the error state.
    const silent = opts?.silent ?? false;
    // Single-flight: never overlap a check with an in-flight download.
    if (state === "downloading" || state === "ready_to_restart") return;
    if (!pluginsRef.current) {
      pluginsRef.current = await loadTauriPlugins();
    }
    const plugins = pluginsRef.current;
    if (!plugins) {
      if (mountedRef.current) setState("unsupported");
      return;
    }

    if (mountedRef.current) {
      setError(null);
      setState("checking");
    }
    try {
      const found = await plugins.updater.check();
      if (!mountedRef.current) return;
      if (!found) {
        updateRef.current = null;
        setUpdate(null);
        setState("up_to_date");
        return;
      }
      // The plugin gives us .version (next), .currentVersion (running),
      // .date (release), .body (release notes). Cast minimally so the
      // hook stays decoupled from the plugin's internal shape.
      const info: AppUpdateInfo = {
        version: found.version,
        currentVersion: found.currentVersion,
        releaseDate: found.date ?? null,
        notes: found.body ?? null,
      };
      // Honour an in-session dismissal — don't pester the user on every
      // re-check if they already said "later".
      if (dismissedVersionRef.current === info.version) {
        updateRef.current = found;
        setUpdate(info);
        setState("up_to_date");
        return;
      }
      updateRef.current = found;
      setUpdate(info);
      setState("available");
    } catch (err) {
      if (!mountedRef.current) return;
      if (isTauriIpcMissing(err)) {
        setState("unsupported");
        return;
      }
      if (silent) {
        // Background check failed — stay quiet, just log for diagnostics.
        console.warn(
          "[updater] background check failed (no toast):",
          err instanceof Error ? err.message : String(err),
        );
        setState("up_to_date");
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
      setState("error");
    }
  }, [state]);

  const installAndRestart = React.useCallback(async (): Promise<void> => {
    const found = updateRef.current;
    const plugins = pluginsRef.current;
    if (!found || !plugins) return;
    if (mountedRef.current) {
      setError(null);
      setDownloaded(0);
      setContentLength(null);
      setState("downloading");
    }
    try {
      await found.downloadAndInstall((event) => {
        if (!mountedRef.current) return;
        if (event.event === "Started") {
          setContentLength(event.data.contentLength ?? null);
        } else if (event.event === "Progress") {
          setDownloaded((d) => d + event.data.chunkLength);
        }
        // "Finished" — fall through to ready_to_restart below.
      });
      if (!mountedRef.current) return;
      setState("ready_to_restart");
      await plugins.process.relaunch();
    } catch (err) {
      if (!mountedRef.current) return;
      if (isTauriIpcMissing(err)) {
        setState("unsupported");
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
      setState("error");
    }
  }, []);

  const dismiss = React.useCallback((): void => {
    if (update) {
      dismissedVersionRef.current = update.version;
    }
    setState("up_to_date");
  }, [update]);

  // Initial check (debounced) + recurring 6h timer. The timer is recreated
  // on state transitions only when ``up_to_date`` so a running download
  // doesn't get interrupted.
  React.useEffect(() => {
    if (state !== "idle" && state !== "up_to_date") return;
    const initialId = window.setTimeout(() => {
      void check({ silent: true });
    }, INITIAL_CHECK_DELAY_MS);
    const intervalId = window.setInterval(() => {
      void check({ silent: true });
    }, RECHECK_INTERVAL_MS);
    return () => {
      window.clearTimeout(initialId);
      window.clearInterval(intervalId);
    };
  }, [check, state]);

  return {
    state,
    update,
    downloaded,
    contentLength,
    error,
    check,
    installAndRestart,
    dismiss,
  };
}
